import { apiFetch } from '@ami/react/core';
import { ProductOptionSetFilter } from './productOptionSetFilter';
import _ from 'lodash';

const _baseEndPoint = 'product-option-sets';

export const ProductOptionSetInventorySettings = Object.freeze({
  doesNotAffectInventory: {
    id: 'DoesNotAffectInventory',
    label: 'Does not affect inventory',
  },
  seriesAffectsInventory: {
    id: 'SeriesAffectsInventory',
    label: 'Series affects inventory',
  },
  seriesAndOptionAffectsInventory: {
    id: 'SeriesAndOptionAffectsInventory',
    label: 'Series and Option affects inventory',
  },
});

export class ProductOptionGroup {
  constructor(json) {
    this.seriesId = json.seriesId;
    this.productOptions = json.productOptions;
    this.productOptionIds = json.productOptionIds
      ? json.productOptionIds
      : json.productOptions.map((x) => x.id);
    this.defaultProductOptionId = json.defaultProductOptionId;
  }

  add(option) {
    this.productOptions = [...this.productOptions, option];
    this.productOptionIds = [...this.productOptionIds, option.id];
  }

  remove(optionId) {
    this.productOptions = this.productOptions.filter((x) => x.id !== optionId);
    this.productOptionIds = this.productOptionIds.filter((x) => x !== optionId);
    if (this.defaultProductOptionId === optionId) {
      this.defaultProductOptionId = null;
    }
  }

  makeDefault(optionId) {
    this.defaultProductOptionId = optionId;
  }

  static createNew(seriesId, productOption) {
    return new ProductOptionGroup({
      seriesId,
      productOptions: [productOption],
      defaultProductOptionId: productOption.id,
    });
  }
}

export class SeriesGroup {
  constructor(json) {
    this.weightCategoryId = json.weightCategoryId;
    this.productOptionGroups = json.productOptionGroups.map(
      (x) => new ProductOptionGroup(x)
    );
    this.defaultSeriesId = json.defaultSeriesId;
  }

  static createNew(weightCategoryId) {
    return new SeriesGroup({
      weightCategoryId,
      productOptionGroups: [],
    });
  }

  getFlatProductOptionIds() {
    const result = [];
    this.productOptionGroups.forEach((productOptionGroup) =>
      productOptionGroup.productOptionIds.forEach((productOptionId) => {
        result.push(productOptionId);
      })
    );
    return result;
  }

  addProductOption(option) {
    const productOptionGroup = this.productOptionGroups.find(
      (x) => x.seriesId === option.seriesId
    );
    if (productOptionGroup) {
      productOptionGroup.add(option);
    } else {
      this.productOptionGroups = [
        ...this.productOptionGroups,
        ProductOptionGroup.createNew(option.seriesId, option),
      ];
    }
    return this;
  }

  removeProductOption(optionId) {
    const productOptionGroup = this.productOptionGroups.find((x) =>
      x.productOptionIds.includes(optionId)
    );
    if (!productOptionGroup)
      throw Error(`ProductOptionId ${optionId} not valid`);
    if (productOptionGroup.productOptionIds.length === 1) {
      this.productOptionGroups = this.productOptionGroups.filter(
        (x) => x.seriesId !== productOptionGroup.seriesId
      );
    } else {
      productOptionGroup.remove(optionId);
    }
    return this;
  }

  clone() {
    return _.cloneDeep(this);
  }
}

export class ProductOptionSet {
  constructor(json) {
    if (json) {
      this.id = json.id;
      this.name = json.name;
      this.publicName = json.publicName;
      this.description = json.description;
      this.isSelectionRequired = json.isSelectionRequired;
      this.isAlwaysVisible = json.isAlwaysVisible;
      this.hideIfSingleOptionAvailable = json.hideIfSingleOptionAvailable;
      this.showPriceAndLeadTime = json.showPriceAndLeadTime;
      this.carrierId = json.carrierId;
      this.seriesGroups = json.seriesGroups.map((x) => new SeriesGroup(x));
      this.inventorySetting = json.inventorySetting;
      this.active = json.active;
      return;
    }
    this.id = null;
    this.name = '';
    this.publicName = '';
    this.description = '';
    this.isSelectionRequired = false;
    this.isAlwaysVisible = true;
    this.hideIfSingleOptionAvailable = false;
    this.showPriceAndLeadTime = true;
    this.carrierId = null;
    this.seriesGroups = [];
    this.inventorySetting = null;
    this.active = true;
  }

  clone() {
    return _.cloneDeep(this);
  }

  setCarrier(id) {
    this.carrierId = id;
  }

  addWeigthCategory(weightCategoryId) {
    if (this.seriesGroups.find((x) => x.weightCategoryId === weightCategoryId))
      throw Error(
        `WeightCategoryId ${weightCategoryId} already added to series group`
      );
    this.seriesGroups = [
      ...this.seriesGroups,
      SeriesGroup.createNew(weightCategoryId),
    ];
    return this;
  }

  replaceSeriesGroup(seriesGroup) {
    this.seriesGroups = this.seriesGroups.map((x) =>
      x.weightCategoryId === seriesGroup.weightCategoryId ? seriesGroup : x
    );
    return this;
  }

  removeWeigthCategory(weightCategoryId) {
    this.seriesGroups = this.seriesGroups.filter(
      (x) => x.weightCategoryId !== weightCategoryId
    );
    return this;
  }

  static async search(filter = new ProductOptionSetFilter(), page) {
    filter.setPaginationParameters(page);
    const result = await apiFetch(`${_baseEndPoint}?${filter.toQueryString()}`);
    return result.map((x) => new ProductOptionSet(x));
  }

  static async getById(id) {
    const entity = await apiFetch(`${_baseEndPoint}/${id}`);
    return new ProductOptionSet(entity);
  }

  static create(item) {
    return apiFetch(_baseEndPoint, 'POST', item);
  }

  update() {
    return apiFetch(`${_baseEndPoint}/${this.id}`, 'PUT', this);
  }

  delete() {
    return apiFetch(`${_baseEndPoint}/${this.id}`, 'DELETE', this);
  }
}
